import React from 'react'
import { getPinnedUidsSet } from '@aytm/app/insights/dashboard/helpers/itemsTransformation'
import { isBenchmark } from './checks'
import { ItemListModel } from '@aytm/app/insights/dashboard/models/BlockModel'
import type {
  DataItem,
  StatItem,
  ItemType,
  Metrics,
  SigTestItem,
  SurveyItemValue,
  AttributeType,
  DataItemAttribute,
  SubjectType,
  ColorsModel
} from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { InsightsDashboardPoolState } from '../duckness/ducks/types'
import { getCurrentBlock } from './requests'
import { getCurrentMethod } from './attributes'

export type GetColumnItem = (uid: string) => ItemType | null

export interface CellData {
  cellStyle: React.CSSProperties | null
  textStyle: React.CSSProperties | null
  responses: number
  respondents: number
  significant: boolean
  lowSample: boolean
  sampleSize: number
  attributeType: string
  attributeTarget: string
  valueType: string | null
  selected: boolean
  pinned: boolean
  itemUid: string | null
  value: unknown
  amount: number
  text: string
  textValue: string | null
  exportValue: unknown
  letters: Array<{
    uid: string
    index: number
    level: number
    letter: string
    text: string
  }>
  showSigtest: boolean
  sigtest: SigTestItem[]
  isDataPending?: boolean
  hasData?: boolean
}

export type GetCellData = (item: ItemType, attribute: string, metrics: Metrics, defaultMetrics: Metrics) => CellData

export type CellDataFactoryParams = {
  items: ItemListModel | null
  data: Record<string, DataItem | string>
  stat: Record<string, StatItem | string>
  subjects: Record<string, SubjectType>
  attributes: Record<string, AttributeType>
  sortByAttribute: string | null
  showSigtest: boolean
  colors: ColorsModel
  currentSplit: string | null
}

export type HasCellDataFactoryParams = {
  data: Record<string, DataItem | string>
  attributes: Record<string, AttributeType>
}

export type HasCellData = (uid: string, attribute: string, metrics: Metrics, defaultMetrics: Metrics) => boolean

export interface ColumnData {
  text: string
  waveText: string
  splitText: string
  description: string
  letter: string
  pinned: boolean
  rank: number
  image: unknown
  isExternalData: boolean
  showSigtest: boolean
}

export type GetColumnData = (item: ItemType) => ColumnData

export type ColumnDataFactoryParams = {
  items: ItemListModel | null
  stat: Record<string, StatItem | string>
  subjects: Record<string, SubjectType>
  attributes: Record<string, AttributeType>
  sortByAttribute: string | null
  showSigtest: boolean
  showImages: boolean
  currentMethod: string | null
}

export const nullCellData: CellData = {
  cellStyle: null,
  textStyle: null,
  responses: 0,
  respondents: 0,
  significant: false,
  lowSample: false,
  sampleSize: 0,
  attributeType: '',
  attributeTarget: '',
  valueType: null,
  selected: false,
  pinned: false,
  itemUid: null, // UID of the item
  value: null,
  amount: null,
  text: '',
  textValue: null,
  exportValue: null,
  letters: [],
  showSigtest: false,
  sigtest: [],
  isDataPending: false,
  hasData: false
}

export const nullCellDataWithPending: CellData = {
  ...nullCellData,
  isDataPending: true
}

export const toFixedValue = (value: null | undefined | string | number, accuracy: number) => {
  if (null == value) {
    return ''
  } else if (0 === value) {
    return '0'
  } else {
    let fixed = Number(value).toFixed(accuracy)
    if (accuracy > 0) {
      while ('0' === fixed.slice(-1)) {
        fixed = fixed.slice(0, -1)
      }
    }
    if ('.' === fixed.slice(-1)) {
      fixed = fixed.slice(0, -1)
    }
    return fixed
  }
}

export const calculateTextValue = (value: number, valueType: DataItemAttribute['type']) =>
  null == value
    ? null
    : 'percent' === valueType
    ? `${toFixedValue(value, 2)}%`
    : 'number' === valueType
    ? `${toFixedValue(value, 2)}`
    : null

export const calculateText = <T extends { text?: string }>(
  value: number,
  valueType: AttributeType['type'],
  attribute: string,
  dataValue: null | T
) =>
  null == value
    ? ''
    : 'Sample size' === attribute
    ? `${toFixedValue(value, 0)}`
    : 'percent' === valueType
    ? `${toFixedValue(value, 1)}%`
    : 'number' === valueType
    ? `${toFixedValue(value, 2)}`
    : dataValue?.text || null

export const calculateStyles = (significantTest: SigTestItem, colors: null | ColorsModel) => {
  const [upperBackgroundColor, lowerBackgroundColor] = [
    colors?.getIn<string>(['upper', 'background']),
    colors?.getIn<string>(['lower', 'background'])
  ]
  const [upperTextColor, lowerTextColor] = [
    colors?.getIn<string>(['upper', 'text']),
    colors?.getIn<string>(['lower', 'text'])
  ]
  const cellStyle = significantTest
    ? 1 === significantTest[1] && upperBackgroundColor
      ? { backgroundColor: upperBackgroundColor }
      : -1 === significantTest[1] && lowerBackgroundColor
      ? { backgroundColor: lowerBackgroundColor }
      : null
    : null
  const textStyle = significantTest
    ? 1 === significantTest[1] && upperTextColor
      ? { color: upperTextColor }
      : -1 === significantTest[1] && lowerTextColor
      ? { color: lowerTextColor }
      : null
    : null
  return { cellStyle, textStyle }
}

export const getFilteredSigtest = (
  sigtest: SigTestItem[],
  {
    items,
    splitPinnedUids,
    pinned,
    currentSplit,
    subjects,
    waveIndex
  }: {
    items: Array<ItemType>
    splitPinnedUids: string[]
    pinned: boolean
    currentSplit: string
    subjects: Record<string, SubjectType>
    waveIndex: number | null
  }
): SigTestItem[] =>
  sigtest
    // Leave only showing items
    .filter(v => items?.some(item => v[0] === item.uid))
    // If pinned items exist then compare only with pinned
    .filter(v => (splitPinnedUids.length ? pinned || splitPinnedUids.includes(v[0]) : true))
    // In split mode compare only inside split
    .filter(v => (currentSplit ? waveIndex === subjects?.[v[0]]?.wave?.index : true))

export const calculateLetters = (
  items: Array<ItemType>,
  filteredSigtest: SigTestItem[],
  subjects: Record<string, SubjectType>
) =>
  items
    .map((item, index) => {
      const found = filteredSigtest.find(s => 1 === s[1] && item.uid === s[0])
      const level = found?.[2]
      const letter = 1 === level ? item.letter?.toLowerCase() : item.letter
      const letterText = subjects?.[item.uid]?.text
      return { uid: item.uid, index: index, level, letter, text: letterText }
    })
    .filter(v => v.level > 0)

export const cellDataFactory = ({
  items,
  data,
  stat,
  subjects,
  attributes,
  sortByAttribute,
  showSigtest,
  currentSplit,
  colors
}: CellDataFactoryParams) => {
  const factoryItems = items?.toJS() || []
  const pinnedUids = getPinnedUidsSet(factoryItems)
  return (item: ItemType, attribute: string, metrics: Metrics, defaultMetrics: Metrics): CellData => {
    if (!item || !factoryItems || !data || !subjects) {
      return nullCellData
    }

    const itemUid = item.uid
    if (!itemUid) {
      return nullCellData
    }

    // Basic data gathering
    const storedData = data[itemUid]
    if (!storedData) {
      return nullCellData
    }

    const storedStat = 'string' === typeof stat[itemUid] ? null : stat[itemUid] || null
    const isDataPending = 'string' === typeof storedData
    if (isDataPending) {
      return nullCellDataWithPending
    }

    const responses = storedData.responses || 0
    const respondents = storedData.respondents || 0

    const key = attributes?.[attribute]?.key || attribute

    // Get metric and attribute data
    const metricId = metrics?.id || defaultMetrics?.id
    const metricData = metricId ? storedData.attributes?.[key]?.metrics?.[metricId] || null : null
    const metricStat = metricId ? storedStat?.[key]?.metrics?.[metricId] || null : null

    const attributeData = storedData.attributes?.[key] || null
    const dataValue = metricId ? metricData : attributeData

    const hasData = null !== dataValue

    const attributeStat = storedStat?.[key] || null
    const statValue = metricId ? metricStat : attributeStat

    const valueType = dataValue?.type || null

    const value = dataValue?.value || 0 === dataValue?.value ? dataValue?.value : null

    const amount = dataValue?.amount || null

    const sigtest: SigTestItem[] = showSigtest ? statValue?.sigtest || [] : []

    const lowSample = showSigtest ? dataValue?.lowSample || false : false

    const sampleSize = dataValue?.sampleSize || 0

    // Calculate text values
    const textValue = calculateTextValue(Array.isArray(value) ? null : Number(value), valueType)
    const text = calculateText(Array.isArray(value) ? null : Number(value), valueType, attribute, dataValue)

    const pinned = pinnedUids.has(itemUid)

    const subject = subjects?.[itemUid] || null

    const attributeType = attributes[attribute]?.type

    const attributeTarget = attributes[attribute]?.target

    const selected = attribute === sortByAttribute

    const waveIndex = currentSplit && subject?.split && subject?.wave ? subject.wave.index : null

    const splitPinnedUids = currentSplit
      ? Array.from(pinnedUids).filter((uid: string) => waveIndex === subjects?.[uid]?.wave?.index)
      : Array.from(pinnedUids)

    // Process sigtest
    const filteredSigtest = getFilteredSigtest(sigtest, {
      items: factoryItems,
      splitPinnedUids,
      pinned: pinnedUids.has(itemUid),
      currentSplit,
      subjects,
      waveIndex
    })

    const significantTest =
      filteredSigtest.find(v => 2 === v[2] && 1 === v[1]) ||
      filteredSigtest.find(v => 2 === v[2] && -1 === v[1]) ||
      null

    const letters = calculateLetters(factoryItems, filteredSigtest, subjects)
    const exportValue =
      'Surveys' === attribute && Array.isArray(value) ? value.map(v => (v as SurveyItemValue).id) : value

    const { cellStyle, textStyle } = calculateStyles(significantTest, colors)

    return {
      cellStyle,
      textStyle,
      responses,
      respondents,
      significant: Boolean(significantTest),
      lowSample,
      sampleSize,
      attributeType,
      attributeTarget,
      valueType,
      selected,
      pinned,
      itemUid,
      value,
      amount,
      text,
      textValue,
      exportValue,
      letters,
      showSigtest,
      isDataPending,
      sigtest,
      hasData
    }
  }
}

export const hasCellDataFactory =
  ({ data, attributes }: HasCellDataFactoryParams) =>
  (uid: string, attribute: string, metrics: Metrics, defaultMetrics: Metrics): boolean => {
    if (!uid || !data) {
      return false
    }
    // Basic data gathering
    const storedData = data[uid]
    if (!storedData) {
      return false
    }

    const isDataPending = 'string' === typeof storedData
    if (isDataPending) {
      return true
    }

    const key = attributes?.[attribute]?.key || attribute

    // Get metric and attribute data
    const metricId = metrics?.id || defaultMetrics?.id
    const metricData = metricId ? storedData.attributes?.[key]?.metrics?.[metricId] || null : null

    const attributeData = storedData.attributes?.[key] || null
    const dataValue = metricId ? metricData : attributeData

    return dataValue?.value || 0 === dataValue?.value ? true : false
  }

export const columnDataFactory = ({
  items,
  stat,
  subjects,
  attributes,
  sortByAttribute,
  showSigtest,
  showImages,
  currentMethod
}: ColumnDataFactoryParams) => {
  const jsItems = items?.toJS() || []
  const pinnedUids = getPinnedUidsSet(jsItems)
  const key = attributes?.[sortByAttribute]?.key || null

  return (item: ItemType) => {
    const itemUid = item?.uid || null

    const pinned = pinnedUids.has(itemUid)

    const subject = itemUid ? subjects?.[itemUid] || null : null

    const text = subject?.wave?.text || subject?.split?.text || subject?.text || ''
    const waveText = subject?.wave?.text || ''
    const splitText = subject?.split?.text || ''

    const rank =
      key && itemUid
        ? currentMethod
          ? (stat?.[itemUid] as StatItem)?.[key]?.metrics?.[currentMethod]?.rank
          : (stat?.[itemUid] as StatItem)?.[key]?.rank
        : null

    const image = showImages && subject?.image ? subject.image : null

    const isExternalData = isBenchmark({ subject })

    const description = subject ? (isExternalData ? 'External data' : null) : null

    const letter = showSigtest ? (item ? item.letter : null) : null

    return { text, waveText, splitText, description, subject, letter, pinned, rank, image, isExternalData, showSigtest }
  }
}

export const getCellData = (
  state: InsightsDashboardPoolState,
  item: ItemType,
  attribute: string,
  metrics: Metrics,
  defaultMetrics: Metrics
): CellData => {
  const block = getCurrentBlock(state)
  const items = block?.getBlockItems()
  const data = state.currentData?.data || {}
  const stat = state.currentData?.stat || {}
  const subjects = state.currentData?.subjects || {}
  const attributes = state.currentData?.attributes || {}
  const sortByAttribute = block?.get<string>('sortByAttribute') || null
  const colors = block?.get<ColorsModel>('colors') || null
  const currentSplit = block?.get<string>('currentSplit') || null
  const showSigtest = block?.get<boolean>('showSigtest') || false
  return cellDataFactory({
    items,
    data,
    stat,
    subjects,
    attributes,
    sortByAttribute,
    showSigtest,
    colors,
    currentSplit
  })(item, attribute, metrics, defaultMetrics)
}

export const getColumnData = (state: InsightsDashboardPoolState, item: ItemType): ColumnData => {
  const block = getCurrentBlock(state)
  const items = block?.getBlockItems()
  const stat = state.currentData?.stat || {}
  const subjects = state.currentData?.subjects || {}
  const attributes = state.currentData?.attributes || {}
  const sortByAttribute = block?.get<string>('sortByAttribute') || null
  const showSigtest = block?.get<boolean>('showSigtest') || false
  const showImages = block?.get<boolean>('showImages') || false
  const currentMethod = getCurrentMethod(state)
  return columnDataFactory({
    items,
    stat,
    subjects,
    attributes,
    sortByAttribute,
    showSigtest,
    showImages,
    currentMethod
  })(item)
}
